import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Search, Upload, ExternalLink, Trash2 } from 'lucide-react';
import { runAiJob } from '../../lib/aiJob';

const OUTCOME = { ganado: 'text-green-400', parcial: 'text-yellow-400', perdido: 'text-red-400', desconocido: 'text-slate-400' };

export const ExpertCard = ({ expert, isAdmin, onChanged, onSelect, selected }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState('');
  const [cases, setCases] = useState(null);

  const loadCases = async () => {
    const r = await api.get(`/experts/${expert.id}/cases`);
    setCases(r.data.cases);
  };

  const research = async () => {
    setBusy('research');
    try {
      const r = await runAiJob(api, `/experts/${expert.id}/research`, undefined, { timeout: 300000 });
      toast.success(`Cerebro leyó ${r.pages_read} fuente(s) y guardó ${r.cases_saved} caso(s)`);
      onChanged(); loadCases();
    } catch (e) {
      toast.error(e.message || 'No se pudo investigar');
    } finally { setBusy(''); }
  };

  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setBusy('upload');
    try {
      const r = await runAiJob(api, `/experts/${expert.id}/upload`, form);
      toast.success(`${r.cases_saved} caso(s) agregados desde ${file.name}`);
      onChanged(); loadCases();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'No se pudo analizar');
    } finally { setBusy(''); ev.target.value = ''; }
  };

  const [bulk, setBulk] = useState(null);
  const pollBulk = async (id) => {
    let data;
    try { ({ data } = await api.get(`/experts/bulk/${id}`)); } catch { setBulk(null); return toast.error('Se perdió el seguimiento de la carga'); }
    setBulk(data);
    if (data.status === 'running') setTimeout(() => pollBulk(id), 4000);
    else if (data.status === 'failed') { toast.error('La carga se interrumpió. Intenta nuevamente.'); onChanged(); loadCases(); }
    else { toast.success(`Carga lista: ${data.saved} caso(s) de ${data.total} archivo(s)`); onChanged(); loadCases(); }
  };
  const bulkUpload = async (ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = '';
    if (!files.length) return;
    if (files.length > 30) return toast.error('Máximo 30 archivos por carga');
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    try {
      const { data } = await api.post(`/experts/${expert.id}/bulk-upload`, form);
      if (data.rejected.length) toast.error(`${data.rejected.length} archivo(s) rechazados`);
      setBulk({ total: data.accepted, done: 0, saved: 0, status: 'running', errors: data.rejected });
      pollBulk(data.bulk_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cargar'); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar a ${expert.name} y sus casos?`)) return;
    await api.delete(`/experts/${expert.id}`);
    onChanged();
  };

  return (
    <div data-testid={`expert-card-${expert.id}`} className={`rounded-xl p-4 border transition-colors ${selected ? 'border-amber-500 bg-amber-500/5' : 'border-slate-700 bg-slate-900/60'}`}>
      <div className="flex items-start justify-between gap-2">
        <label className="flex items-start gap-2 cursor-pointer min-w-0">
          <input type="checkbox" checked={selected} onChange={() => onSelect(expert.id)} className="mt-1 accent-amber-500" data-testid={`expert-select-${expert.id}`} />
          <div className="min-w-0">
            <p className="text-white font-semibold">{expert.name}</p>
            <p className="text-xs text-amber-300">{expert.specialty}{expert.auto_detected ? ' · detectado por IA' : ''}</p>
          </div>
        </label>
        {isAdmin && <button onClick={remove} className="text-slate-500 hover:text-red-400" aria-label="Eliminar experto" data-testid={`expert-delete-${expert.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
      {expert.bio && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{expert.bio}</p>}
      <p data-testid={`expert-stats-${expert.id}`} className="text-xs text-slate-300 mt-2">
        {expert.cases_total} caso(s) · <span className="text-green-400">{expert.cases_won} ganados</span> · <span className="text-red-400">{expert.cases_lost} perdidos</span>
      </p>
      <div className="flex gap-2 mt-3">
        {!expert.uploads_only && <button data-testid={`expert-research-btn-${expert.id}`} onClick={research} disabled={!!busy} className="flex-1 text-xs bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg py-1.5 flex items-center justify-center gap-1 disabled:opacity-50">
          {busy === 'research' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}{busy === 'research' ? 'Investigando (1-3 min)...' : 'Investigar en fuentes públicas'}
        </button>}
        <label className={`flex-1 text-xs border border-slate-600 text-slate-300 rounded-lg py-1.5 flex items-center justify-center gap-1 cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          {busy === 'upload' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Subir documento
          <input data-testid={`expert-upload-${expert.id}`} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={upload} />
        </label>
      </div>
      <label className="mt-2 w-full text-xs border border-amber-500/40 text-amber-300 rounded-lg py-1.5 flex items-center justify-center gap-1 cursor-pointer hover:bg-amber-500/10">
        <Upload className="w-3 h-3" />Carga masiva (hasta 30 sentencias)
        <input data-testid={`expert-bulk-upload-${expert.id}`} type="file" multiple accept=".pdf,.docx,.txt" className="hidden" onChange={bulkUpload} />
      </label>
      {bulk && (
        <div data-testid={`expert-bulk-progress-${expert.id}`} className="mt-2 text-xs text-slate-300">
          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${bulk.total ? Math.round((bulk.done / bulk.total) * 100) : 100}%` }} /></div>
          <p className="mt-1">{bulk.status === 'running' ? `Analizando ${bulk.done}/${bulk.total}...` : `Listo: ${bulk.saved} caso(s) guardados`}{bulk.errors?.length ? ` · ${bulk.errors.length} con error` : ''}</p>
        </div>
      )}
      <button data-testid={`expert-cases-btn-${expert.id}`} onClick={cases ? () => setCases(null) : loadCases} className="text-xs text-amber-400 mt-2">{cases ? 'Ocultar casos' : 'Ver casos'}</button>
      {cases && (
        <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
          {cases.length === 0 && <p className="text-xs text-slate-500">Sin casos aún.</p>}
          {cases.map((c) => (
            <div key={c.id} className="bg-slate-800/60 rounded-lg p-2 text-xs">
              <div className="flex justify-between gap-2"><p className="text-white font-medium">{c.title}</p><span className={OUTCOME[c.outcome]}>{c.outcome}</span></div>
              <p className="text-slate-400">{c.summary}</p>
              {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-cyan-400 flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" />Fuente</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
